import React, { useState } from 'react'
import {
  Container,
  Grid,
  Box,
  Typography,
  InputBase,
  useTheme
} from '@mui/material'
import ForumRounded from '@mui/icons-material/ForumRounded'
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded'
import ConfirmationNumberRounded from '@mui/icons-material/ConfirmationNumberRounded'
import { IconTile } from '../../components/UI/Depth'
import { cardSx, depth } from '../../utils/depth'
import useGlobalStyles from '../../utils/globalStyles'
import SearchIcon from '@mui/icons-material/Search'
import Header from '../../components/Headers/Header'
import UserTicketCard from '../../components/SupportTicket/UserTicketCard'
import TicketCard from '../../components/SupportTicket/TicketCard'
import TicketChatModal from '../../components/SupportTicket/TicketChatModal'
import CustomLoader from '../../components/Loader/CustomLoader'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  getTicketUsersWithLatest,
  getSingleUserSupportTickets
} from '../../apollo/queries'
import { markTicketMessagesAsRead } from '../../apollo/mutations'
import { withTranslation } from 'react-i18next'

const GET_TICKET_USERS = gql`
  ${getTicketUsersWithLatest}
`
const GET_USER_TICKETS = gql`
  ${getSingleUserSupportTickets}
`
const MARK_TICKET_MESSAGES_AS_READ = gql`
  ${markTicketMessagesAsRead}
`

const PanelHeader = ({ icon, tone, title, count }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2.5,
      py: 1.75,
      background: depth.headerBackground,
      borderBottom: '1px solid rgba(16,24,40,0.06)'
    }}>
    <IconTile icon={icon} tone={tone} size={36} />
    <Typography sx={{ fontSize: 16, fontWeight: 700, flexGrow: 1 }}>
      {title}
    </Typography>
    {typeof count === 'number' ? (
      <Box
        component="span"
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          bgcolor: 'primary.light',
          color: 'primary.main'
        }}>
        {count}
      </Box>
    ) : null}
  </Box>
)

const CustomerSupport = ({ t }) => {
  const theme = useTheme()
  const globalClasses = useGlobalStyles()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)

  const scrollAreaStyles = {
    flex: 1,
    height: 0,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehaviorY: 'contain',
    pr: 1
  }

  const { data: userData, loading: userLoading } = useQuery(GET_TICKET_USERS, {
    variables: { input: { search: searchQuery || null, page: 1, limit: 100 } },
    pollInterval: 30000
  })

  const {
    data: ticketData,
    loading: ticketLoading,
    refetch: refetchTickets
  } = useQuery(GET_USER_TICKETS, {
    variables: {
      input: { userId: selectedUserId, filters: { page: 1, limit: 100 } }
    },
    skip: !selectedUserId
  })
  const [markAsRead] = useMutation(MARK_TICKET_MESSAGES_AS_READ)

  const handleUserClick = userId => {
    setSelectedUserId(userId)
  }

  const handleTicketClick = ticket => {
    setSelectedTicketId(ticket._id)
    setChatOpen(true)
    markAsRead({
      variables: { ticketId: ticket._id }
    })
      .then(() => {
        if (refetchTickets) refetchTickets()
      })
      .catch(error => {
        console.error('Failed to mark ticket messages as read:', error)
      })
  }

  return (
    <>
      <Box
        sx={{
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
        <Header />
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            pb: 3,
            flex: 1,
            minHeight: { md: 0 },
            display: 'flex',
            overflow: 'hidden'
          }}>
          <Grid
            container
            spacing={3}
            sx={{
              flex: 1,
              minHeight: { md: 0 },
              alignItems: 'stretch',
              overflow: 'hidden'
            }}>
            {/* Users List Column */}
            <Grid
              item
              xs={12}
              md={4}
              lg={3}
              sx={{
                display: 'flex',
                height: { md: '100%' },
                minHeight: { md: 0 },
                overflow: 'hidden'
              }}>
              <Box
                sx={{
                  ...cardSx,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xs: '50vh', md: '100%' },
                  maxHeight: { xs: '50vh', md: '100%' },
                  minHeight: 0,
                  width: '100%',
                  overflow: 'hidden'
                }}>
                <PanelHeader
                  icon={ForumRounded}
                  tone="orange"
                  title={t('Support Chats')}
                  count={userData?.getTicketUsersWithLatest?.users.length}
                />
                <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                  <InputBase
                    placeholder={t('Search users...')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className={globalClasses.input}
                    sx={{ mt: 0 }}
                    startAdornment={
                      <SearchIcon
                        sx={{ color: 'text.disabled', fontSize: 20, mr: 1 }}
                      />
                    }
                  />
                </Box>
                <Box sx={{ ...scrollAreaStyles, px: 2, pb: 2 }}>
                  {userLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CustomLoader />
                    </Box>
                  ) : (
                    userData?.getTicketUsersWithLatest?.users.map(user => (
                      <UserTicketCard
                        key={user._id}
                        user={user}
                        isSelected={selectedUserId === user._id}
                        onClick={() => handleUserClick(user._id)}
                      />
                    ))
                  )}
                  {!userLoading &&
                    userData?.getTicketUsersWithLatest?.users.length === 0 && (
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ mt: 4, color: theme.palette.text.secondary }}>
                        {t('No users found')}
                      </Typography>
                    )}
                </Box>
              </Box>
            </Grid>

            {/* Tickets List Column */}
            <Grid
              item
              xs={12}
              md={8}
              lg={9}
              sx={{
                display: 'flex',
                height: { md: '100%' },
                minHeight: { md: 0 },
                overflow: 'hidden'
              }}>
              <Box
                sx={{
                  ...cardSx,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xs: '60vh', md: '100%' },
                  maxHeight: { xs: '60vh', md: '100%' },
                  minHeight: 0,
                  width: '100%',
                  overflow: 'hidden'
                }}>
                {!selectedUserId ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                    gap={1}
                    px={3}
                    height="100%">
                    <IconTile icon={SupportAgentRounded} size={72} />
                    <Typography
                      sx={{
                        mt: 2,
                        fontSize: 18,
                        fontWeight: 700,
                        color: 'text.primary'
                      }}>
                      {t('Select a user to view tickets')}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>
                      {t(
                        'All support requests from the selected user will appear here'
                      )}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <PanelHeader
                      icon={ConfirmationNumberRounded}
                      tone="blue"
                      title={t('Tickets')}
                      count={
                        ticketData?.getSingleUserSupportTickets?.tickets.length
                      }
                    />
                    <Box sx={{ ...scrollAreaStyles, p: 2.5 }}>
                      {ticketLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                          <CustomLoader />
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {ticketData?.getSingleUserSupportTickets?.tickets.map(
                            ticket => (
                              <Grid item xs={12} key={ticket._id}>
                                <TicketCard
                                  ticket={ticket}
                                  isSelected={selectedTicketId === ticket._id}
                                  onClick={() => handleTicketClick(ticket)}
                                />
                              </Grid>
                            )
                          )}
                        </Grid>
                      )}
                      {!ticketLoading &&
                        ticketData?.getSingleUserSupportTickets?.tickets
                          .length === 0 && (
                          <Typography
                            variant="body1"
                            align="center"
                            sx={{ mt: 8, color: theme.palette.text.secondary }}>
                            {t('This user has no tickets yet')}
                          </Typography>
                        )}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <TicketChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        ticketId={selectedTicketId}
      />
    </>
  )
}

export default withTranslation()(CustomerSupport)
